import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'controller/utils/@reduxjs/toolkit';
import {
  useInjectReducer,
  useInjectSaga,
} from 'controller/utils/redux-injectors';
import { success, error } from 'core/components/Notify';

import CustomResponse from 'core/utils/types/CustomResponse';
import { communicationSetupSaga } from './saga';
import { CommunicationSetup, CommunicationSetupState } from './types';
import { convertNaNIntoNumber } from 'core/utils/constants';
import { JWTPayload } from 'features/Authentication/slice/types';

export const initialState: CommunicationSetupState = {
  communicationDataList: [],
  ACCESS_TOKEN: "",
  listResponse: null,
  communicationDataViewList: [],
  viewListResponse: null,
  callView: false,

  isLoadingCommunicationDataList: false,
  errorCommunicationDataList: undefined,
  pageIndex: 0,
  pageSize: 10,
  autoConnect: {},

  communicationData: undefined,
  isLoadingCommunicationData: false,

  isUpsertLoading: false,
  errorCommunicationData: undefined,

  chatConversationData: undefined,
  createCommunicationSetupSuccess: false,

  navigate: false,
  isDeletedCommunicationData: false,
  isDeletingCommunicationData: false,
  errorDeletingCommunicationData: {} as CustomResponse,
};

const slice = createSlice({
  name: 'communicationSetup',
  initialState,
  reducers: {
    fetchCommunicationSetupListStart(
      state,
      action: PayloadAction<{
        ID: number | string;
        URL: string;
        ACADEMIC_YEAR_ID?: number;
        TABLE_CALL?: boolean;
        authData?: JWTPayload;
        TABLE_DATA?: { page_size?: string | number, page_index?: string | number }
        action?: "VIEW_API_CALL" | "VIEW_AND_LIST_API_CALL";
        storeListInDifferentState?: boolean;
        DATA?: any;
      }>,
    ) {
      state.callView = false;
      if (["facebook/pages", 'instagram/accounts'].includes(action?.payload?.URL) && !!action?.payload?.ID) {
        state.ACCESS_TOKEN = action?.payload?.ID;
      }
      if (!action?.payload?.TABLE_CALL) {
        state.isDeletedCommunicationData = false;
        state.isLoadingCommunicationDataList = true;
      } else {
        state.pageIndex = convertNaNIntoNumber(Number(action?.payload?.TABLE_DATA?.page_index));
        state.pageSize = convertNaNIntoNumber(Number(action?.payload?.TABLE_DATA?.page_size));
      }
    },

    fetchCommunicationSetupListSuccess(
      state,
      action: PayloadAction<{ listData: [], data: object, storeListInDifferentState?: boolean }>,
    ) {
      state.isLoadingCommunicationDataList = false;
      // state.communicationDataList = action.payload?.listData;
      // state.listResponse = action.payload?.data;
      if (action.payload?.storeListInDifferentState) {
        state.communicationDataViewList = action.payload?.listData;
        state.viewListResponse = action.payload?.data;
      } else {
        state.communicationDataList = action.payload?.listData;
        state.listResponse = action.payload?.data;
      }
    },
    fetchCommunicationSetupListFailure(
      state,
      action: PayloadAction<CustomResponse>,
    ) {
      state.isLoadingCommunicationDataList = false;
      state.errorCommunicationData = action.payload;
    },

    fetchCommunicationSetupOneStart(
      state,
      action: PayloadAction<{ ID: number; URL: string, authData?: JWTPayload }>,
    ) {
      state.callView = false;
      state.isLoadingCommunicationData = true;
    },
    fetchCommunicationSetupOneSuccess(
      state,
      action: PayloadAction<CommunicationSetup>,
    ) {
      state.isLoadingCommunicationData = false;
      state.communicationData = action.payload;
      state.listResponse = action.payload;
    },
    fetchCommunicationSetupOneFailure(
      state,
      action: PayloadAction<CustomResponse>,
    ) {
      state.isLoadingCommunicationData = false;
      state.errorCommunicationData = action.payload;
    },
    setChatConversationData(state, action: PayloadAction<any>) {
      state.chatConversationData = action.payload;
    },

    createCommunicationSetupStart(
      state,
      action: PayloadAction<{
        URL: string;
        data: CommunicationSetup | any;
        action?: string;
        authData?: any;
        message: string;
      }>,
    ) {
      state.errorCommunicationData = undefined;
      state.isUpsertLoading = true;
    },
    // createCommunicationSetupSuccess(state, action: PayloadAction<string>) {
    //   state.isUpsertLoading = false;
    //   state.navigate = true;
    //   state.createCommunicationSetupSuccess = true;
    //   success(action.payload, true);
    // },

    createCommunicationSetupSuccess(state, action: any) {
      state.callView = true;
      state.isUpsertLoading = false;
      state.ACCESS_TOKEN = "";
      if (action.payload.URL === "google/update_info") {
        state.listResponse = null;
      }
      if (action.payload !== "PAYMENT_SUCCESS") {
        state.navigate = true;
      }
      if (action.payload.data?.EVENT_ID) {
        state.communicationData = action.payload.data;
      }
      // if (!action.payload.flag) {
      //   // success(action.payload.message, true);
      // }
    },

    createCommunicationSetupFailure(
      state,
      action: PayloadAction<CustomResponse>,
    ) {
      state.isUpsertLoading = false;
      state.errorCommunicationData = action.payload;
      error(action.payload, true);
    },

    deleteCommunicationSetupStart(
      state,
      action: PayloadAction<{ URL: string; ID: number, authData?: JWTPayload }>,
    ) {
      state.errorDeletingCommunicationData = undefined;
      state.isDeletingCommunicationData = true;
    },
    deleteCommunicationSetupSuccess(state, action: PayloadAction<any>) {
      state.isDeletingCommunicationData = false;
      state.isDeletedCommunicationData = true;
      success('Deleted Successfully', true);
      // successfully Deleted | Alert Message
    },
    deleteCommunicationSetupFailure(
      state,
      action: PayloadAction<CustomResponse>,
    ) {
      state.isDeletingCommunicationData = false;
      state.errorDeletingCommunicationData = action.payload;
    },
    setAutoConnect(state, action) {
      state.autoConnect = action.payload;
    },

    clearCommunicationViewEditData(state) {
      state.communicationData = undefined;
      state.isLoadingCommunicationData = false;
      // state.isCustomLoadingData = false;
      // state.notificationLoader = false;
    },

    clearCommunicationData(state) {
      state.communicationDataList = [] as CommunicationSetup[];
      state.communicationDataViewList = [] as CommunicationSetup[];
      state.viewListResponse = undefined;
      state.communicationData = undefined;
      state.isLoadingCommunicationData = false;
      state.pageIndex = 0;
      state.pageSize = 10;
      state.ACCESS_TOKEN = "";
    },

    clearState(state) {
      state.communicationDataList = [];
      state.communicationData = undefined;
      state.communicationDataViewList = [] as CommunicationSetup[];
      state.viewListResponse = undefined;
      state.listResponse = undefined;
      state.isLoadingCommunicationData = false;
      state.pageIndex = 0;
      state.pageSize = 10;
      state.ACCESS_TOKEN = "";
    },

    clearAutoConnect(state) {
      state.autoConnect = {};
    },

    clearViewData(state) {
      state.communicationData = undefined;
      state.listResponse = undefined;
      state.isLoadingCommunicationData = false;
    },

    clearSocialHubData(state) {
      state.communicationDataList = [];

      state.ACCESS_TOKEN = "";
      state.listResponse = null;
    },

    setNavigate(state, action: PayloadAction<boolean>) {
      state.navigate = action.payload;
    },
  },
});

export const communicationSetupActions = slice.actions;
export const setChatConversationData = slice.actions.setChatConversationData;

export const useCommunicationSetupSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: communicationSetupSaga });
  return { actions: slice.actions };
};
